import { useState, useRef, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { RefreshCw, Loader2, Clock, AlertTriangle } from 'lucide-react'
import { useUploadVersion, VersionConflictError, clearNegotiationContext, useActionItems, useIssues } from '../../hooks/useNegotiations'
import { useOfficeDocument } from '../../hooks/useOfficeDocument'
import { useWordCommentSync } from '../../hooks/useWordCommentSync'
import { useBackendToWordSync } from '../../hooks/useBackendToWordSync'
import { useUnreadComments } from '../../hooks/useUnreadComments'
import { useNegotiationSSE } from '../../hooks/useNegotiationSSE'
import { listComments } from '../../services/commentsApi'
import { exportNegotiationPdf } from '../../services/negotiationsApi'
import type { NegotiationComment } from '../../services/negotiationsApi'
import { SendBackBanner } from './SendBackBanner'
import { CommentPanel } from './CommentPanel'
import { VersionHistoryPanel } from './VersionHistoryPanel'
import { RedlinePanel } from './RedlinePanel'
import { NegotiationStatsPanel } from './NegotiationStatsPanel'
import { NegotiationActivityLog } from './NegotiationActivityLog'
import { ActionItemsPanel } from './ActionItemsPanel'
import { IssuesPanel } from './IssuesPanel'
import { ReadyStatus } from './ReadyStatus'
import { SendToSigningButton } from './SendToSigningButton'
import { CancelNegotiationButton } from './CancelNegotiationButton'
import ImportAnalysisSheet from './ImportAnalysisSheet'
import { DocumentDropdown } from './DocumentDropdown'
import { usePresence } from '../../hooks/usePresence'
import { NegotiationTabBar, type NegotiationTab } from './NegotiationTabBar'
import type { NegotiationDetail, NegotiationDocumentInfo } from '../../services/negotiationsApi'

interface SendNegotiationSendbackProps {
  negotiationId: number
  activeNegotiation: NegotiationDetail
  expectedVersion: number
  title: string
  actorId?: string
  showToast?: (type: 'success' | 'error' | 'info', message: string, action?: { label: string; onAction: () => void }) => void
  onVersionSent: (newVersion: number) => void
  onConflict: (serverVersion: number, sentBy?: string, createdAt?: string) => void
  onSending: (message: string) => void
  onCancelled: () => void
  onSwitchToSigning?: () => void
  onNegotiationActive?: (active: boolean) => void
}

export function SendNegotiationSendback({
  negotiationId,
  activeNegotiation,
  expectedVersion,
  title,
  actorId,
  showToast,
  onVersionSent,
  onConflict,
  onSending,
  onCancelled,
  onSwitchToSigning,
  onNegotiationActive,
}: SendNegotiationSendbackProps) {
  const { t } = useTranslation()
  const { getDocumentBlob, isReading } = useOfficeDocument()
  const uploadVersionMut = useUploadVersion()

  const [activeTab, setActiveTab] = useState<NegotiationTab>('comments')
  const [activeDocumentId, setActiveDocumentId] = useState<number | null>(null)
  const [error, setError] = useState('')
  const [isExporting, setIsExporting] = useState(false)

  // 6a: Auto-save version note to localStorage
  const versionNoteKey = `kobsign-version-note-${negotiationId}`
  const [versionNote, setVersionNote] = useState(() => {
    try { return localStorage.getItem(versionNoteKey) || '' } catch { return '' }
  })
  const versionNoteTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const updateVersionNote = useCallback((value: string) => {
    setVersionNote(value)
    if (versionNoteTimeoutRef.current) clearTimeout(versionNoteTimeoutRef.current)
    versionNoteTimeoutRef.current = setTimeout(() => {
      try {
        if (value.trim()) localStorage.setItem(versionNoteKey, value)
        else localStorage.removeItem(versionNoteKey)
      } catch { /* best-effort */ }
    }, 500)
  }, [versionNoteKey])
  const [showImportAnalysis, setShowImportAnalysis] = useState(false)

  // Badge counts for tab bar
  const { data: actionItems } = useActionItems(negotiationId)
  const { data: issues } = useIssues(negotiationId)
  const openTaskCount = actionItems?.filter((i) => i.status !== 'DONE').length ?? 0
  const dealBreakerCount = issues?.filter((i) => i.status === 'OPEN' && i.is_deal_breaker).length ?? 0

  // Presence
  const activeUsers = usePresence(negotiationId, actorId)

  // Build tag metadata for Word comment sync
  const knownTags = (activeNegotiation.parties ?? []).map((p) => p.tag_name)
  const tagColors = Object.fromEntries(
    (activeNegotiation.parties ?? []).map((p) => [p.tag_name.toLowerCase(), p.color]),
  )

  // Word comment sync (#-tag shortcut)
  const commentSyncRefetchRef = useRef<(() => void) | null>(null)
  const wordCommentSync = useWordCommentSync({
    negotiationId,
    currentVersion: expectedVersion,
    knownTags,
    tagColors,
    enabled: true,
    onCommentSynced: () => commentSyncRefetchRef.current?.(),
  })

  // SSE real-time updates with toast notifications
  const handleSSEEvent = useCallback((eventType: string, data: Record<string, unknown>) => {
    switch (eventType) {
      case 'negotiation.comment_added':
        showToast?.('info', t('negotiate.sseToast.commentAdded', {
          author: (data.author_display_name as string) || t('negotiate.sseToast.someone'),
        }), {
          label: t('negotiate.sseToast.reply', 'Reply'),
          onAction: () => setActiveTab('comments'),
        })
        break
      case 'negotiation.version_created':
        showToast?.('info', t('negotiate.sseToast.versionCreated', {
          version: data.version_number ?? '?',
        }))
        break
      case 'negotiation.status_changed':
        showToast?.('info', t('negotiate.sseToast.statusChanged', {
          status: data.new_status as string ?? '',
        }))
        break
      case 'negotiation.party_ready':
        showToast?.('success', t('negotiate.sseToast.partyReady', {
          party: (data.party_label as string) || '',
        }))
        break
      case 'negotiation.party_added':
        showToast?.('info', t('negotiate.sseToast.partyAdded', {
          party: (data.party_label as string) || '',
        }))
        break
    }
  }, [showToast, t])

  useNegotiationSSE({
    negotiationId,
    enabled: true,
    onEvent: handleSSEEvent,
  })

  // Backend → Word comment sync (inserts web/mobile comments as native Word comments)
  const { data: allComments } = useQuery<NegotiationComment[]>({
    queryKey: ['comments-flat', negotiationId],
    queryFn: async () => {
      const res = await listComments(negotiationId, undefined, { limit: 200 })
      // listComments returns PaginatedComments when limit is provided
      return (res as { results: NegotiationComment[] }).results ?? (res as NegotiationComment[])
    },
    staleTime: 30_000,
  })

  const backendWordSync = useBackendToWordSync({
    negotiationId,
    comments: allComments ?? [],
    enabled: true,
  })

  // 6b: Unread badge for comments tab
  const unread = useUnreadComments(negotiationId, allComments, actorId)

  // Next upcoming milestone (for compact bar in banner area)
  const nextMilestone = useMemo(() => {
    const ms = activeNegotiation.milestones
    if (!ms?.length) return null
    return ms
      .filter(m => m.status === 'UPCOMING' || m.status === 'DUE_SOON' || m.status === 'OVERDUE')
      .sort((a, b) => new Date(a.deadline).getTime() - new Date(b.deadline).getTime())[0] ?? null
  }, [activeNegotiation.milestones])

  const handleSendBack = async () => {
    setError('')
    onSending(t('negotiate.uploadingVersion'))

    try {
      const result = await uploadVersionMut.mutateAsync({
        negotiationId,
        expectedVersion,
        title,
        actorId,
        note: versionNote.trim() || undefined,
        getDocumentBlob,
      })

      setVersionNote('')
      try { localStorage.removeItem(versionNoteKey) } catch { /* */ }
      onVersionSent(result.version_number)
      showToast?.('success', t('negotiate.versionSent'))
    } catch (err) {
      if (err instanceof VersionConflictError) {
        onConflict(err.currentVersion, err.sentBy, err.createdAt)
      } else {
        const msg = err instanceof Error ? err.message : String(err)
        setError(msg)
        showToast?.('error', msg)
      }
    }
  }

  const handleExportPdf = async () => {
    setIsExporting(true)
    try {
      const blob = await exportNegotiationPdf(negotiationId)
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `${title || 'negotiation'}_v${expectedVersion}.pdf`
      a.click()
      URL.revokeObjectURL(url)
      showToast?.('success', t('negotiate.exportPdf', 'Export as PDF'))
    } catch {
      showToast?.('error', t('negotiate.exportFailed', 'Export failed'))
    } finally {
      setIsExporting(false)
    }
  }

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* Compact banner */}
      <div className="shrink-0 px-3 py-2 space-y-2">
        <SendBackBanner
          negotiation={activeNegotiation}
          actorId={actorId}
          showToast={showToast}
          wordSync={{
            isSupported: wordCommentSync.isSupported || backendWordSync.isSupported,
            isActive: wordCommentSync.isActive,
            syncedCount: wordCommentSync.syncedCount + backendWordSync.syncedCount,
          }}
          activeUsers={activeUsers}
        />

        {/* Compact milestone bar — visible when milestones exist */}
        {nextMilestone && (
          <div className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-neutral-50 dark:bg-neutral-800/50 border border-neutral-150 dark:border-neutral-700">
            {nextMilestone.status === 'OVERDUE' ? (
              <AlertTriangle size={11} className="text-error-500 shrink-0" />
            ) : (
              <Clock size={11} className={`shrink-0 ${nextMilestone.status === 'DUE_SOON' ? 'text-warning-500' : 'text-neutral-400'}`} />
            )}
            <span className={`text-[10px] font-medium truncate ${
              nextMilestone.status === 'OVERDUE'
                ? 'text-error-600 dark:text-error-400'
                : nextMilestone.status === 'DUE_SOON'
                  ? 'text-warning-600 dark:text-warning-400'
                  : 'text-neutral-500 dark:text-neutral-400'
            }`}>
              {t('negotiate.nextMilestone', 'Next')}: {nextMilestone.title}
            </span>
            <span className="flex-1" />
            <span className={`text-[9px] shrink-0 ${nextMilestone.status === 'OVERDUE' ? 'text-error-500' : 'text-neutral-400'}`}>
              {new Date(nextMilestone.deadline).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
            </span>
          </div>
        )}

        {/* Error message */}
        {error && (
          <div className="p-2 rounded bg-error-50 dark:bg-error-900/20 border border-error-200 dark:border-error-800">
            <p className="text-xs text-error-600 dark:text-error-400">{error}</p>
          </div>
        )}

        {/* Document switcher for multi-doc negotiations */}
        {activeNegotiation.documents && activeNegotiation.documents.length > 1 && (
          <DocumentDropdown
            documents={activeNegotiation.documents}
            activeDocumentId={activeDocumentId}
            onSelect={(doc: NegotiationDocumentInfo) => setActiveDocumentId(doc.id)}
          />
        )}
      </div>

      {/* Tab bar */}
      <NegotiationTabBar
        activeTab={activeTab}
        onTabChange={(tab) => {
          setActiveTab(tab)
          if (tab === 'comments') unread.markAllRead()
        }}
        unreadCount={activeTab !== 'comments' ? unread.unreadCount : 0}
        openTaskCount={openTaskCount}
        dealBreakerCount={dealBreakerCount}
        onExport={handleExportPdf}
        isExporting={isExporting}
      />

      {/* Tab content — fills remaining space */}
      {activeTab === 'comments' && (
        <CommentPanel
          negotiationId={negotiationId}
          currentVersion={expectedVersion}
          parties={activeNegotiation.parties}
          actorId={actorId}
          showToast={showToast}
        />
      )}

      {activeTab === 'versions' && (
        <div className="flex-1 overflow-y-auto">
          {/* Send new version */}
          <div className="px-3 py-2 space-y-1.5 border-b border-neutral-100 dark:border-neutral-800">
            <input
              type="text"
              value={versionNote}
              onChange={(e) => updateVersionNote(e.target.value)}
              maxLength={500}
              placeholder={t('negotiate.versionNotePlaceholder', 'What changed? (optional)')}
              className="w-full px-2 py-1.5 text-[11px] border border-neutral-200 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-800 focus:outline-none focus:ring-1 focus:ring-primary-500 placeholder:text-neutral-400"
            />
            <div className="flex gap-1.5">
              <button
                onClick={handleSendBack}
                disabled={isReading || uploadVersionMut.isPending}
                className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium text-primary-700 dark:text-primary-300 bg-primary-50 dark:bg-primary-900/30 hover:bg-primary-100 dark:hover:bg-primary-900/50 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed rounded-lg border border-primary-200 dark:border-primary-800 transition-all duration-200"
              >
                {uploadVersionMut.isPending ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : (
                  <RefreshCw size={14} />
                )}
                {t('negotiate.sendBackButton')}
              </button>
              <button
                onClick={() => setShowImportAnalysis(true)}
                disabled={showImportAnalysis}
                className="flex items-center gap-1 px-2 py-2 text-xs font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 dark:bg-blue-900/20 dark:text-blue-300 dark:hover:bg-blue-900/30 rounded-lg border border-blue-200 dark:border-blue-800 transition-colors disabled:opacity-50"
                title={t('negotiations.aiImport.title', 'AI Import Analysis')}
              >
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
                <span>AI</span>
              </button>
            </div>
          </div>

          {/* AI Import Analysis */}
          {showImportAnalysis && (
            <div className="px-3 py-2">
              <ImportAnalysisSheet
                negotiationId={negotiationId}
                onDismiss={() => setShowImportAnalysis(false)}
                onApplied={() => setShowImportAnalysis(false)}
              />
            </div>
          )}

          {/* Version history */}
          <VersionHistoryPanel negotiationId={negotiationId} title={title} alwaysExpanded />
        </div>
      )}

      {activeTab === 'changes' && (
        <div className="flex-1 overflow-y-auto">
          <RedlinePanel negotiationId={negotiationId} currentVersion={expectedVersion} alwaysExpanded />
        </div>
      )}

      {activeTab === 'status' && (
        <div className="flex-1 overflow-y-auto">
          <ReadyStatus
            negotiationId={negotiationId}
            parties={activeNegotiation.parties}
            actorId={actorId}
          />
          <SendToSigningButton
            negotiationId={negotiationId}
            parties={activeNegotiation.parties}
            actorId={actorId}
            onSent={() => {
              clearNegotiationContext()
              onNegotiationActive?.(false)
              onSwitchToSigning?.()
            }}
          />
          <CancelNegotiationButton
            negotiationId={negotiationId}
            actorId={actorId}
            ownerActorId={activeNegotiation.owner?.koblink_id}
            onCancelled={() => {
              clearNegotiationContext()
              onNegotiationActive?.(false)
              onCancelled()
            }}
          />
        </div>
      )}

      {activeTab === 'tasks' && (
        <div className="flex-1 overflow-y-auto">
          <ActionItemsPanel
            negotiationId={negotiationId}
            parties={activeNegotiation.parties}
            actorId={actorId}
            alwaysExpanded
          />
        </div>
      )}

      {activeTab === 'issues' && (
        <div className="flex-1 overflow-y-auto">
          <IssuesPanel negotiationId={negotiationId} alwaysExpanded />
        </div>
      )}

      {activeTab === 'stats' && (
        <div className="flex-1 overflow-y-auto">
          <NegotiationStatsPanel negotiationId={negotiationId} alwaysExpanded />
          <NegotiationActivityLog negotiationId={negotiationId} alwaysExpanded />
        </div>
      )}
    </div>
  )
}
